import { z } from "zod";
import { ApiError, request } from "./http";
import type { ReplaySource } from "@atrix.dev/replay-player";
import { postSse, streamSse } from "./sse";
import type { QueryIr, QueryResponse } from "./ir";
import {
  type Annotation,
  AnnotationListResponse,
  AskConversation,
  AskConversationList,
  type AskConversationSummary,
  AskEvent,
  type AskInput,
  type CreateExperimentInput,
  type CreateFlagInput,
  Experiment,
  ExperimentListResponse,
  ExperimentPlan,
  type ExperimentPlanInput,
  ExperimentResults,
  type ExperimentTransition,
  type ExperimentTransitionInput,
  Flag,
  FlagListResponse,
  type CreateMetricInput,
  Metric,
  MetricListResponse,
  type ReplayListParams,
  ReplaysPage,
  type UpdateExperimentInput,
  type UpdateFlagEnvironmentInput,
  type UpdateFlagInput,
  type Cohort,
  type CohortSize,
  type Dashboard,
  type DashboardSummary,
  type DashboardTile,
  type CatalogEvent,
  type CatalogProperty,
  type GroupType,
  type GroupsPage,
  type IdentityGraph,
  type LiveEvent,
  MembersResponse,
  OAuthConnection,
  AcceptedResponse,
  type Person,
  type PersonEventsPage,
  type PersonsPage,
  RevokeOthersResponse,
  type SavedInsight,
  Session,
  type SqlResult,
  type StaticUploadResult,
  type UnmergeResult,
  CreatedToken,
  CreateProjectResponse,
  EventsWire,
  Me,
  Org,
  PersonalToken,
  Project,
  PropertiesWire,
  PublicKey,
  QueryResponseSchema,
  Region,
  RegionToken,
  RotateKeyResponse,
  toEventDefinitions,
  toPropertyDefinitions,
  type ChangePasswordInput,
  type CohortDefinition,
  type CreateCatalogEventInput,
  type CreateCohortInput,
  type CreateDashboardInput,
  type CreateInsightInput,
  type CreateTileInput,
  type DashboardLayoutInput,
  type ForgotPasswordInput,
  type InsightListParams,
  type Member,
  type ResetPasswordInput,
  type SqlInput,
  type UnmergeInput,
  type UpdateCatalogEventInput,
  type UpdateCatalogPropertyInput,
  type UpdateCohortInput,
  type UpdateDashboardInput,
  type UpdateInsightInput,
  type CreateOrgInput,
  type CreateProjectInput,
  type CreateTokenInput,
  type EnvironmentName,
  type EventDefinition,
  type PropertyDefinition,
  type SignInInput,
  type SignUpInput,
} from "./schemas";
import {
  BillingOverview,
  CatalogEventWire,
  CatalogListWire,
  CatalogSyncWire,
  CheckoutOutcome,
  CohortListWire,
  CohortPreviewWire,
  CohortWire,
  DashboardListWire,
  DashboardWire,
  GroupTypesWire,
  GroupsWire,
  IdentityWire,
  InsightListWire,
  InsightWire,
  LiveEventWire,
  PersonEventsWire,
  PersonWire,
  PersonsWire,
  SqlWire,
  StaticUploadWire,
  TileWire,
  UnmergeWire,
  freshSize,
  propertyInput,
  toCatalogEvent,
  toCatalogProperties,
  toCohort,
  toCohortDefinitionWire,
  toDashboard,
  toDashboardSummary,
  toGroupType,
  toGroupsPage,
  toIdentityGraph,
  toLiveEvent,
  toPerson,
  toPersonEventsPage,
  toPersonsPage,
  toSavedInsight,
  toSqlResult,
  toTile,
  toUnmergeResult,
  type CatalogSyncResult,
} from "./wire";

/**
 * Everything the console asks of the backend. Two implementations: `createHttpApi`
 * (the real control-plane + region APIs) and the fixture adapter behind
 * `VITE_USE_FIXTURES=1`. Screens only ever see this interface.
 */
export interface Api {
  // control plane — session cookie `atx_session`
  me(): Promise<Me | null>;
  signUp(input: SignUpInput): Promise<Me>;
  signIn(input: SignInInput): Promise<Me>;
  signOut(): Promise<void>;
  regions(): Promise<Region[]>;
  createOrg(input: CreateOrgInput): Promise<Org>;
  listProjects(orgId: string): Promise<Project[]>;
  createProject(orgId: string, input: CreateProjectInput): Promise<CreateProjectResponse>;
  getProject(projectId: string): Promise<Project>;
  listKeys(projectId: string): Promise<PublicKey[]>;
  rotateKey(projectId: string, keyId: string): Promise<RotateKeyResponse>;
  revokeKey(projectId: string, keyId: string): Promise<PublicKey>;
  listTokens(): Promise<PersonalToken[]>;
  createToken(input: CreateTokenInput): Promise<CreatedToken>;
  revokeToken(tokenId: string): Promise<void>;
  /** `scopes` narrows the token (each must be held, else 403); omitted = every regional scope held. */
  regionToken(projectId: string, environment: EnvironmentName, scopes?: string[]): Promise<RegionToken>;
  // region data plane — bearer region token, called directly (never via the control plane)
  events(rt: RegionToken): Promise<EventDefinition[]>;
  /** Event properties (optionally of one event) plus person properties. */
  properties(rt: RegionToken, event?: string): Promise<PropertyDefinition[]>;
  query(rt: RegionToken, ir: QueryIr, signal?: AbortSignal): Promise<QueryResponse>;

  // ── Wave 2: account (control plane) ──
  changePassword(input: ChangePasswordInput): Promise<void>;
  requestPasswordReset(input: ForgotPasswordInput): Promise<void>;
  resetPassword(input: ResetPasswordInput): Promise<void>;
  verifyEmail(token: string): Promise<void>;
  resendVerification(): Promise<void>;
  listSessions(): Promise<Session[]>;
  revokeSession(sessionId: string): Promise<void>;
  revokeOtherSessions(): Promise<number>;
  listMembers(orgId: string): Promise<Member[]>;
  listConnections(): Promise<OAuthConnection[]>;
  revokeConnection(grantId: string): Promise<void>;

  // ── Wave 2: configuration (control plane) ──
  listInsights(projectId: string, params?: InsightListParams): Promise<SavedInsight[]>;
  getInsight(projectId: string, insightId: string): Promise<SavedInsight>;
  createInsight(projectId: string, input: CreateInsightInput): Promise<SavedInsight>;
  updateInsight(projectId: string, insightId: string, input: UpdateInsightInput): Promise<SavedInsight>;
  deleteInsight(projectId: string, insightId: string): Promise<void>;
  setInsightFavorite(projectId: string, insightId: string, favorite: boolean): Promise<SavedInsight>;

  listDashboards(projectId: string): Promise<DashboardSummary[]>;
  getDashboard(projectId: string, dashboardId: string): Promise<Dashboard>;
  createDashboard(projectId: string, input: CreateDashboardInput): Promise<Dashboard>;
  updateDashboard(projectId: string, dashboardId: string, input: UpdateDashboardInput): Promise<Dashboard>;
  deleteDashboard(projectId: string, dashboardId: string): Promise<void>;
  saveDashboardLayout(projectId: string, dashboardId: string, input: DashboardLayoutInput): Promise<Dashboard>;
  addTile(projectId: string, dashboardId: string, input: CreateTileInput): Promise<DashboardTile>;
  updateTile(projectId: string, dashboardId: string, tileId: string, body: string): Promise<DashboardTile>;
  removeTile(projectId: string, dashboardId: string, tileId: string): Promise<void>;

  listCohorts(projectId: string): Promise<Cohort[]>;
  getCohort(projectId: string, cohortId: string): Promise<Cohort>;
  createCohort(projectId: string, input: CreateCohortInput): Promise<Cohort>;
  updateCohort(projectId: string, cohortId: string, input: UpdateCohortInput): Promise<Cohort>;
  deleteCohort(projectId: string, cohortId: string): Promise<void>;

  listCatalogEvents(projectId: string): Promise<CatalogEvent[]>;
  createCatalogEvent(projectId: string, input: CreateCatalogEventInput): Promise<CatalogEvent>;
  updateCatalogEvent(projectId: string, eventId: string, input: UpdateCatalogEventInput): Promise<CatalogEvent>;
  listCatalogProperties(projectId: string): Promise<CatalogProperty[]>;
  updateCatalogProperty(projectId: string, propertyId: string, input: UpdateCatalogPropertyInput): Promise<CatalogProperty>;
  /** Merge what the region has seen into the plan now (the control plane also does it every 10 minutes). */
  syncCatalog(projectId: string, environment: EnvironmentName): Promise<CatalogSyncResult>;
  /** Latest size for `environment`; recalculated through the control plane when stale (cohorts:write). */
  cohortSize(projectId: string, environment: EnvironmentName, cohortId: string): Promise<CohortSize>;

  // ── Wave 2: billing (control plane, per org) ──
  billing(orgId: string): Promise<BillingOverview>;
  setSpendCap(orgId: string, spendCapMinor: number): Promise<number>;
  checkout(orgId: string, plan: string): Promise<CheckoutOutcome>;
  billingPortal(orgId: string): Promise<string>;

  // ── Wave 2: region data plane ──
  previewCohort(rt: RegionToken, definition: CohortDefinition): Promise<CohortSize>;
  /** Upload to the region (replacing the list), then recalculate through the control plane. */
  uploadStaticCohort(rt: RegionToken, projectId: string, cohortId: string, distinctIds: string[]): Promise<StaticUploadResult>;
  /** Streams live events until `signal` aborts. Resolves when the stream ends. */
  tailEvents(rt: RegionToken, opts: { event?: string }, onEvent: (e: LiveEvent) => void, signal: AbortSignal): Promise<void>;
  listPersons(rt: RegionToken, params: { search?: string; cursor?: string }): Promise<PersonsPage>;
  getPerson(rt: RegionToken, personId: string): Promise<Person>;
  personEvents(rt: RegionToken, personId: string, before?: string): Promise<PersonEventsPage>;
  identityGraph(rt: RegionToken, personId: string): Promise<IdentityGraph>;
  unmerge(rt: RegionToken, personId: string, input: UnmergeInput): Promise<UnmergeResult>;
  groupTypes(rt: RegionToken): Promise<GroupType[]>;
  listGroups(rt: RegionToken, params: { typeIndex: number; search?: string; cursor?: string }): Promise<GroupsPage>;
  sql(rt: RegionToken, input: SqlInput, signal?: AbortSignal): Promise<SqlResult>;

  // ── Wave 3: definitions forwarded to Ask (control plane) ──
  listMetrics(projectId: string): Promise<Metric[]>;
  /** POST /app/projects/{p}/metrics (defs-metric MetricCreateRequest) — e.g. from a saved insight. */
  createMetric(projectId: string, input: CreateMetricInput): Promise<Metric>;
  listAnnotations(projectId: string): Promise<Annotation[]>;

  // ── Wave 3: Ask (region, scope ai:read) ──
  /** Streams one turn. Rejects with ApiError 503 `ai_not_configured` / `ai_disabled` when Ask is off here. */
  ask(rt: RegionToken, input: AskInput, onEvent: (e: AskEvent) => void, signal: AbortSignal): Promise<void>;
  listConversations(rt: RegionToken): Promise<AskConversationSummary[]>;
  getConversation(rt: RegionToken, conversationId: string): Promise<AskConversation>;

  // ── Wave 3: replays (region, scope replays:read) ──
  listReplays(rt: RegionToken, params: ReplayListParams): Promise<ReplaysPage>;
  /** A player source for one session; `token` is called for every request (tokens live ≤ 15 min). */
  replaySource(token: () => Promise<RegionToken>, sessionId: string): Promise<ReplaySource>;

  // ── Wave 3: flags (control plane) — SHAPE: from flags branch ──
  listFlags(projectId: string, params?: { archived?: boolean; search?: string }): Promise<Flag[]>;
  getFlag(projectId: string, flagId: string): Promise<Flag>;
  createFlag(projectId: string, input: CreateFlagInput): Promise<Flag>;
  updateFlag(projectId: string, flagId: string, input: UpdateFlagInput): Promise<Flag>;
  updateFlagEnvironment(projectId: string, flagId: string, environment: EnvironmentName, input: UpdateFlagEnvironmentInput): Promise<Flag>;
  archiveFlag(projectId: string, flagId: string): Promise<void>;

  // ── Wave 3: experiments (control plane) — SHAPE: from flags branch ──
  listExperiments(projectId: string): Promise<Experiment[]>;
  getExperiment(projectId: string, experimentId: string): Promise<Experiment>;
  createExperiment(projectId: string, input: CreateExperimentInput): Promise<Experiment>;
  updateExperiment(projectId: string, experimentId: string, input: UpdateExperimentInput): Promise<Experiment>;
  transitionExperiment(
    projectId: string,
    experimentId: string,
    transition: ExperimentTransition,
    input: ExperimentTransitionInput,
  ): Promise<Experiment>;
  experimentResults(projectId: string, experimentId: string, refresh?: boolean): Promise<ExperimentResults>;
  planExperiment(projectId: string, input: ExperimentPlanInput): Promise<ExperimentPlan>;
}

/** `?a=1&b=x` from the defined entries, or "". */
export function queryString(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === false) continue;
    q.set(k, v === true ? "1" : String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

const Empty = z.unknown();

export function createHttpApi(base = ""): Api {
  const cp = (path: string) => `${base}${path}`;
  const region = (rt: RegionToken, path: string) => `${rt.region_api_url.replace(/\/$/, "")}${path}`;
  const enc = encodeURIComponent;

  return {
    async me() {
      try {
        return await request(Me, cp("/app/me"));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    signUp: (input) => request(Me, cp("/app/auth/signup"), { method: "POST", body: input }),
    signIn: (input) => request(Me, cp("/app/auth/login"), { method: "POST", body: input }),
    signOut: async () => {
      await request(Empty, cp("/app/auth/logout"), { method: "POST" });
    },
    regions: () => request(z.object({ regions: z.array(Region) }), cp("/app/regions")).then((r) => r.regions),
    createOrg: (input) => request(Org, cp("/app/orgs"), { method: "POST", body: input }),
    listProjects: (orgId) =>
      request(z.object({ projects: z.array(Project) }), cp(`/app/orgs/${enc(orgId)}/projects`)).then((r) => r.projects),
    createProject: (orgId, input) =>
      request(CreateProjectResponse, cp(`/app/orgs/${enc(orgId)}/projects`), { method: "POST", body: input }),
    getProject: (projectId) => request(Project, cp(`/app/projects/${enc(projectId)}`)),
    listKeys: (projectId) =>
      request(z.object({ keys: z.array(PublicKey) }), cp(`/app/projects/${enc(projectId)}/keys`)).then((r) => r.keys),
    rotateKey: (projectId, keyId) =>
      request(RotateKeyResponse, cp(`/app/projects/${enc(projectId)}/keys/${enc(keyId)}/rotate`), {
        method: "POST",
        body: { keep_old: false },
      }),
    revokeKey: (projectId, keyId) =>
      request(PublicKey, cp(`/app/projects/${enc(projectId)}/keys/${enc(keyId)}/revoke`), { method: "POST" }),
    listTokens: () =>
      request(z.object({ tokens: z.array(PersonalToken) }), cp("/app/tokens")).then((r) => r.tokens),
    createToken: (input) =>
      request(CreatedToken, cp("/app/tokens"), {
        method: "POST",
        // The API takes "omit" for a token that never expires, not null.
        body: { ...input, expires_in_days: input.expires_in_days ?? undefined },
      }),
    revokeToken: async (tokenId) => {
      await request(Empty, cp(`/app/tokens/${enc(tokenId)}`), { method: "DELETE" });
    },
    regionToken: (projectId, environment, scopes) =>
      request(RegionToken, cp(`/app/projects/${enc(projectId)}/region-token`), {
        method: "POST",
        body: scopes ? { environment, scopes } : { environment },
      }),
    events: (rt) =>
      request(EventsWire, region(rt, "/schema/events?days=30"), { token: rt.token }).then(toEventDefinitions),
    properties: async (rt, event) => {
      const [ev, person] = await Promise.all([
        request(PropertiesWire, region(rt, `/schema/properties?type=event${event ? `&event=${enc(event)}` : ""}`), {
          token: rt.token,
        }),
        request(PropertiesWire, region(rt, "/schema/properties?type=person"), { token: rt.token }),
      ]);
      return [...toPropertyDefinitions(ev), ...toPropertyDefinitions(person)];
    },
    query: (rt, ir, signal) =>
      request(QueryResponseSchema, region(rt, "/query"), { method: "POST", body: { query: ir }, token: rt.token, signal }),

    // ── account ──
    changePassword: async (input) => {
      await request(Empty, cp("/app/auth/change-password"), { method: "POST", body: input });
    },
    requestPasswordReset: async (input) => {
      await request(AcceptedResponse, cp("/app/auth/password-reset"), { method: "POST", body: input });
    },
    resetPassword: async (input) => {
      await request(Empty, cp("/app/auth/password-reset/confirm"), { method: "POST", body: input });
    },
    verifyEmail: async (token) => {
      await request(Empty, cp("/app/auth/verify-email"), { method: "POST", body: { token } });
    },
    resendVerification: async () => {
      await request(Empty, cp("/app/auth/verify-email/send"), { method: "POST" });
    },
    listSessions: () =>
      request(z.object({ sessions: z.array(Session) }), cp("/app/auth/sessions")).then((r) => r.sessions),
    revokeSession: async (sessionId) => {
      await request(Empty, cp(`/app/auth/sessions/${enc(sessionId)}`), { method: "DELETE" });
    },
    revokeOtherSessions: () =>
      request(RevokeOthersResponse, cp("/app/auth/sessions/revoke-others"), { method: "POST" }).then((r) => r.revoked),
    listMembers: (orgId) => request(MembersResponse, cp(`/app/orgs/${enc(orgId)}/members`)).then((r) => r.members),
    listConnections: () =>
      request(z.object({ connections: z.array(OAuthConnection) }), cp("/app/oauth/connections")).then((r) => r.connections),
    revokeConnection: async (grantId) => {
      await request(Empty, cp(`/app/oauth/connections/${enc(grantId)}`), { method: "DELETE" });
    },

    // ── saved insights ──
    listInsights: (projectId, params = {}) =>
      request(
        InsightListWire,
        cp(
          `/app/projects/${enc(projectId)}/insights${queryString({
            search: params.search,
            kind: params.kind,
            tag: params.tag,
            favorited: params.favorites ? "true" : undefined,
            limit: 200,
          })}`,
        ),
      ).then((r) => r.insights.map(toSavedInsight)),
    getInsight: (projectId, insightId) =>
      request(InsightWire, cp(`/app/projects/${enc(projectId)}/insights/${enc(insightId)}`)).then(toSavedInsight),
    createInsight: (projectId, { query_ir, viz, ...rest }) =>
      request(InsightWire, cp(`/app/projects/${enc(projectId)}/insights`), {
        method: "POST",
        body: { ...rest, query: query_ir, viz },
      }).then(toSavedInsight),
    updateInsight: (projectId, insightId, { query_ir, ...rest }) =>
      request(InsightWire, cp(`/app/projects/${enc(projectId)}/insights/${enc(insightId)}`), {
        method: "PATCH",
        body: { ...rest, ...(query_ir ? { query: query_ir } : {}) },
      }).then(toSavedInsight),
    deleteInsight: async (projectId, insightId) => {
      await request(Empty, cp(`/app/projects/${enc(projectId)}/insights/${enc(insightId)}`), { method: "DELETE" });
    },
    setInsightFavorite: (projectId, insightId, favorite) =>
      request(InsightWire, cp(`/app/projects/${enc(projectId)}/insights/${enc(insightId)}/favorite`), {
        method: "PUT",
        body: { favorite },
      }).then(toSavedInsight),

    // ── dashboards ──
    listDashboards: (projectId) =>
      request(DashboardListWire, cp(`/app/projects/${enc(projectId)}/dashboards`)).then((r) =>
        r.dashboards.map((d) => toDashboardSummary(projectId, d)),
      ),
    getDashboard: (projectId, dashboardId) =>
      request(DashboardWire, cp(`/app/projects/${enc(projectId)}/dashboards/${enc(dashboardId)}`)).then(toDashboard),
    createDashboard: (projectId, input) =>
      request(DashboardWire, cp(`/app/projects/${enc(projectId)}/dashboards`), { method: "POST", body: input }).then(toDashboard),
    updateDashboard: (projectId, dashboardId, { pinned, filters, ...rest }) =>
      request(DashboardWire, cp(`/app/projects/${enc(projectId)}/dashboards/${enc(dashboardId)}`), {
        method: "PATCH",
        body: {
          ...rest,
          ...(pinned !== undefined ? { is_home: pinned } : {}),
          ...(filters !== undefined ? { date_range: filters.date_range ?? null } : {}),
        },
      }).then(toDashboard),
    deleteDashboard: async (projectId, dashboardId) => {
      await request(Empty, cp(`/app/projects/${enc(projectId)}/dashboards/${enc(dashboardId)}`), { method: "DELETE" });
    },
    saveDashboardLayout: (projectId, dashboardId, input) =>
      request(DashboardWire, cp(`/app/projects/${enc(projectId)}/dashboards/${enc(dashboardId)}/layout`), {
        method: "POST",
        body: input,
      }).then(toDashboard),
    addTile: (projectId, dashboardId, input) =>
      request(TileWire, cp(`/app/projects/${enc(projectId)}/dashboards/${enc(dashboardId)}/tiles`), {
        method: "POST",
        body: input,
      }).then(toTile),
    updateTile: (projectId, dashboardId, tileId, body) =>
      request(TileWire, cp(`/app/projects/${enc(projectId)}/dashboards/${enc(dashboardId)}/tiles/${enc(tileId)}`), {
        method: "PATCH",
        body: { body },
      }).then(toTile),
    removeTile: async (projectId, dashboardId, tileId) => {
      await request(Empty, cp(`/app/projects/${enc(projectId)}/dashboards/${enc(dashboardId)}/tiles/${enc(tileId)}`), {
        method: "DELETE",
      });
    },

    // ── cohorts ──
    listCohorts: (projectId) =>
      request(CohortListWire, cp(`/app/projects/${enc(projectId)}/cohorts`)).then((r) => r.cohorts.map(toCohort)),
    getCohort: (projectId, cohortId) =>
      request(CohortWire, cp(`/app/projects/${enc(projectId)}/cohorts/${enc(cohortId)}`)).then(toCohort),
    createCohort: (projectId, { definition, ...rest }) =>
      request(CohortWire, cp(`/app/projects/${enc(projectId)}/cohorts`), {
        method: "POST",
        body: { ...rest, definition: definition ? toCohortDefinitionWire(definition) : null },
      }).then(toCohort),
    updateCohort: (projectId, cohortId, { definition, ...rest }) =>
      request(CohortWire, cp(`/app/projects/${enc(projectId)}/cohorts/${enc(cohortId)}`), {
        method: "PATCH",
        body: { ...rest, ...(definition ? { definition: toCohortDefinitionWire(definition) } : {}) },
      }).then(toCohort),
    deleteCohort: async (projectId, cohortId) => {
      await request(Empty, cp(`/app/projects/${enc(projectId)}/cohorts/${enc(cohortId)}`), { method: "DELETE" });
    },
    cohortSize: async (projectId, environment, cohortId) => {
      const path = cp(`/app/projects/${enc(projectId)}/cohorts/${enc(cohortId)}`);
      const cohort = await request(CohortWire, path);
      const fresh = freshSize(cohort, environment);
      if (fresh) return fresh;
      try {
        // The control plane asks the region to materialise the cohort and records the size.
        const calc = await request(CohortWire, `${path}/calculate`, { method: "POST", body: { environment } });
        const size = freshSize(calc, environment, true);
        if (size) return size;
        throw new ApiError(503, "calculation_failed", calc.calculation?.error ?? "The cohort could not be calculated.");
      } catch (err) {
        // Viewers may not recalculate: show the last size there is, for this environment.
        const c = cohort.calculation;
        if (err instanceof ApiError && err.status === 403 && c?.size != null && c.environment === environment) {
          return { count: c.size, total: null, calculated_at: c.calculated_at };
        }
        throw err;
      }
    },

    // ── catalog ──
    listCatalogEvents: (projectId) =>
      request(CatalogListWire, cp(`/app/projects/${enc(projectId)}/catalog/events`)).then((r) => r.events.map(toCatalogEvent)),
    createCatalogEvent: (projectId, input) =>
      request(CatalogEventWire, cp(`/app/projects/${enc(projectId)}/catalog/events`), { method: "POST", body: input }).then(
        toCatalogEvent,
      ),
    updateCatalogEvent: (projectId, eventId, input) =>
      request(CatalogEventWire, cp(`/app/projects/${enc(projectId)}/catalog/events/${enc(eventId)}`), {
        method: "PATCH",
        body: input,
      }).then(toCatalogEvent),
    listCatalogProperties: (projectId) =>
      request(CatalogListWire, cp(`/app/projects/${enc(projectId)}/catalog/events`)).then((r) =>
        r.events.flatMap(toCatalogProperties),
      ),
    updateCatalogProperty: async (projectId, propertyId, input) => {
      // Properties belong to their event: the event's PATCH replaces its whole property list.
      const { events } = await request(CatalogListWire, cp(`/app/projects/${enc(projectId)}/catalog/events`));
      const owner = events.find((e) => e.properties.some((p) => p.id === propertyId));
      if (!owner) throw new ApiError(404, "not_found", "That property is no longer in the plan.");
      const properties = owner.properties.map((p) =>
        propertyInput(
          p.id === propertyId
            ? { ...p, ...(input.description !== undefined ? { description: input.description } : {}), ...(input.pii ? { pii: input.pii } : {}) }
            : p,
        ),
      );
      const updated = await request(CatalogEventWire, cp(`/app/projects/${enc(projectId)}/catalog/events/${enc(owner.id)}`), {
        method: "PATCH",
        body: { properties },
      });
      const prop = toCatalogProperties(updated).find((p) => p.name === owner.properties.find((x) => x.id === propertyId)?.name);
      if (!prop) throw new ApiError(404, "not_found", "That property is no longer in the plan.");
      return prop;
    },
    syncCatalog: (projectId, environment) =>
      request(CatalogSyncWire, cp(`/app/projects/${enc(projectId)}/catalog/sync`), { method: "POST", body: { environment } }),

    // ── billing (control plane, per org) ──
    billing: (orgId) => request(BillingOverview, cp(`/app/orgs/${enc(orgId)}/billing`)),
    setSpendCap: (orgId, spendCapMinor) =>
      request(z.object({ spend_cap_minor: z.number(), previous_spend_cap_minor: z.number() }), cp(`/app/orgs/${enc(orgId)}/billing/cap`), {
        method: "PATCH",
        body: { spend_cap_minor: spendCapMinor },
      }).then((r) => r.spend_cap_minor),
    checkout: (orgId, plan) =>
      request(CheckoutOutcome, cp(`/app/orgs/${enc(orgId)}/billing/checkout`), { method: "POST", body: { plan } }),
    billingPortal: (orgId) =>
      request(z.object({ url: z.url() }), cp(`/app/orgs/${enc(orgId)}/billing/portal`), { method: "POST" }).then((r) => r.url),

    // ── region: cohorts, live events, people, groups, SQL ──
    previewCohort: (rt, definition) =>
      request(CohortPreviewWire, region(rt, "/cohorts/preview"), {
        method: "POST",
        body: { definition: toCohortDefinitionWire(definition) },
        token: rt.token,
      }),
    uploadStaticCohort: async (rt, projectId, cohortId, distinctIds) => {
      // Ids go straight to the region (never through the control plane), 10,000 per request.
      let stored = 0;
      for (let i = 0; i < distinctIds.length; i += 10_000) {
        const r = await request(StaticUploadWire, region(rt, `/cohorts/${enc(cohortId)}/static`), {
          method: "POST",
          body: { distinct_ids: distinctIds.slice(i, i + 10_000), mode: i === 0 ? "replace" : "add" },
          token: rt.token,
        });
        stored = r.total;
      }
      const calc = await request(CohortWire, cp(`/app/projects/${enc(projectId)}/cohorts/${enc(cohortId)}/calculate`), {
        method: "POST",
        body: { environment: rt.environment },
      });
      const members = calc.calculation?.size ?? 0;
      return { matched: members, unmatched: Math.max(0, stored - members), members };
    },
    tailEvents: (rt, { event }, onEvent, signal) =>
      streamSse(region(rt, `/events/live${queryString({ event })}`), {
        token: rt.token,
        signal,
        onMessage: (m) => {
          if (m.event !== "event") return;
          try {
            const parsed = LiveEventWire.safeParse(JSON.parse(m.data));
            if (parsed.success) onEvent(toLiveEvent(parsed.data));
          } catch {
            /* a malformed message is skipped, not fatal to the stream */
          }
        },
      }),
    listPersons: (rt, { search, cursor }) =>
      request(PersonsWire, region(rt, `/persons${queryString({ search, cursor, limit: 50 })}`), { token: rt.token }).then(
        toPersonsPage,
      ),
    getPerson: (rt, personId) =>
      request(PersonWire, region(rt, `/persons/${enc(personId)}?events=1`), { token: rt.token }).then(toPerson),
    personEvents: (rt, personId, before) =>
      request(PersonEventsWire, region(rt, `/persons/${enc(personId)}/events${queryString({ before, limit: 50 })}`), {
        token: rt.token,
      }).then(toPersonEventsPage),
    identityGraph: (rt, personId) =>
      request(IdentityWire, region(rt, `/persons/${enc(personId)}/identity`), { token: rt.token }).then(toIdentityGraph),
    unmerge: (rt, personId, input) =>
      request(UnmergeWire, region(rt, `/persons/${enc(personId)}/unmerge`), { method: "POST", body: input, token: rt.token }).then(
        (w) => toUnmergeResult(personId, w),
      ),
    groupTypes: (rt) =>
      request(GroupTypesWire, region(rt, "/groups/types"), { token: rt.token }).then((r) => r.group_types.map(toGroupType)),
    listGroups: (rt, { typeIndex, search }) =>
      request(GroupsWire, region(rt, `/groups${queryString({ group_type_index: typeIndex, search, limit: 200 })}`), {
        token: rt.token,
      }).then(toGroupsPage),
    sql: (rt, input, signal) =>
      request(SqlWire, region(rt, "/sql"), { method: "POST", body: input, token: rt.token, signal }).then(toSqlResult),

    // ── Wave 3 ──
    listMetrics: (projectId) =>
      request(MetricListResponse, cp(`/app/projects/${enc(projectId)}/metrics`)).then((r) => r.metrics),
    createMetric: (projectId, input) => request(Metric, cp(`/app/projects/${enc(projectId)}/metrics`), { method: "POST", body: input }),
    listAnnotations: (projectId) =>
      request(AnnotationListResponse, cp(`/app/projects/${enc(projectId)}/annotations`)).then((r) => r.annotations),

    ask: (rt, input, onEvent, signal) =>
      postSse(region(rt, "/ask"), {
        token: rt.token,
        body: input,
        signal,
        onMessage: (m) => {
          const e = parseAskEvent(m.data);
          if (e) onEvent(e);
        },
      }),
    listConversations: (rt) =>
      request(AskConversationList, region(rt, "/ask/conversations"), { token: rt.token }).then((r) => r.conversations),
    getConversation: (rt, conversationId) =>
      request(AskConversation, region(rt, `/ask/conversations/${enc(conversationId)}`), { token: rt.token }),

    listReplays: (rt, params) =>
      request(ReplaysPage, region(rt, `/replays${replayQuery(params)}`), { token: rt.token }),
    replaySource: async (token, sessionId) => {
      const { httpSource } = await import("@atrix.dev/replay-player");
      const first = await token();
      return httpSource({
        baseUrl: first.region_api_url,
        sessionId,
        token: async () => (await token()).token,
      });
    },

    // flags — SHAPE: from flags branch
    listFlags: (projectId, params = {}) =>
      request(
        FlagListResponse,
        cp(`/app/projects/${enc(projectId)}/flags${queryString({ archived: params.archived ? "true" : undefined, search: params.search })}`),
      ).then((r) => r.flags),
    getFlag: (projectId, flagId) => request(Flag, cp(`/app/projects/${enc(projectId)}/flags/${enc(flagId)}`)),
    createFlag: (projectId, input) => request(Flag, cp(`/app/projects/${enc(projectId)}/flags`), { method: "POST", body: input }),
    updateFlag: (projectId, flagId, input) =>
      request(Flag, cp(`/app/projects/${enc(projectId)}/flags/${enc(flagId)}`), { method: "PATCH", body: input }),
    updateFlagEnvironment: (projectId, flagId, environment, input) =>
      request(Flag, cp(`/app/projects/${enc(projectId)}/flags/${enc(flagId)}/environments/${enc(environment)}`), {
        method: "PATCH",
        body: input,
      }),
    archiveFlag: async (projectId, flagId) => {
      await request(Empty, cp(`/app/projects/${enc(projectId)}/flags/${enc(flagId)}`), { method: "DELETE" });
    },

    // experiments — SHAPE: from flags branch
    listExperiments: (projectId) =>
      request(ExperimentListResponse, cp(`/app/projects/${enc(projectId)}/experiments`)).then((r) => r.experiments),
    getExperiment: (projectId, experimentId) =>
      request(Experiment, cp(`/app/projects/${enc(projectId)}/experiments/${enc(experimentId)}`)),
    createExperiment: (projectId, input) =>
      request(Experiment, cp(`/app/projects/${enc(projectId)}/experiments`), { method: "POST", body: input }),
    updateExperiment: (projectId, experimentId, input) =>
      request(Experiment, cp(`/app/projects/${enc(projectId)}/experiments/${enc(experimentId)}`), { method: "PATCH", body: input }),
    transitionExperiment: (projectId, experimentId, transition, input) =>
      request(Experiment, cp(`/app/projects/${enc(projectId)}/experiments/${enc(experimentId)}/${transition}`), {
        method: "POST",
        body: input,
      }),
    experimentResults: (projectId, experimentId, refresh) =>
      request(
        ExperimentResults,
        cp(`/app/projects/${enc(projectId)}/experiments/${enc(experimentId)}/results${queryString({ refresh: refresh ? "true" : undefined })}`),
      ),
    planExperiment: (projectId, input) =>
      request(ExperimentPlan, cp(`/app/projects/${enc(projectId)}/experiments/plan`), { method: "POST", body: input }),
  };
}

/** One SSE `data:` payload → an AskEvent, or null for a malformed / unknown one (skipped, not fatal). */
export function parseAskEvent(data: string): AskEvent | null {
  try {
    const parsed = AskEvent.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** ReplayListParams → `?…` (booleans as `true`/`false`, which serde's bool parser accepts). */
export function replayQuery(p: ReplayListParams): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(p) as [string, string | number | boolean | undefined][]) {
    if (v === undefined || v === "") continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}
